import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import styled from 'styled-components';
import AppState from '@webapp/store/state';
import { SetActiveLabel } from '@webapp/store/record-labels/action-creators';
import LabelListItem from '@components/label/label-list-item/index';
import ArtistMenuSelectorCtrl from '@webapp/controllers/artist/artist-menu-selector';

import { RecordLabel } from '@common/types/record-label/record-label';
import SimpleArtist from '@common/types/artist/simple-artist';
import LabelMenuOptions from './label-menu-options';
import { EntitySubscription } from '@common/types/subscription-manager/list-subscriptions-response';

type LabelArtistsDict = Record<string, (SimpleArtist | LabelArtistsItem)[]>;

interface LabelArtistsItem {
  id: string;
  name: string;
  avatar: string;
  type: string;
  shortId: string;
  lastAccess?: number;
  created_at: Date;
  updated_at: Date;
}

interface LabelMenuSelectorCtrlProps {
  className?: string;
  currentUserId: string;
  activeLabel?: string;
  labels: RecordLabel[];
  labelArtistsDict: LabelArtistsDict;
  artists: SimpleArtist[];
  activeArtist?: SimpleArtist;
  labelSubscriptions?: EntitySubscription[];
  setActive: (label: RecordLabel) => SetActiveLabel;
  onChange: () => void;
}

const mapStateToProps = (state: AppState) => {
  const recordLabelsObj =
    state.recordLabels.list
      ?.filter((l) => !l.isDeleted)
      .sort(function (a, b) {
        if (a.name && b.name && a.name.toLowerCase() < b.name.toLowerCase())
          return -1;
        if (a.name && b.name && a.name.toLowerCase() > b.name.toLowerCase())
          return 1;
        return 0;
      })
      .reduce((acc, recordLabel) => {
        if (!acc[recordLabel.id]) {
          acc[recordLabel.id] = recordLabel;
        }
        return acc;
      }, {}) ?? {};
  const labels: RecordLabel[] = Object.values(recordLabelsObj) ?? [];
  const relations = state.recordLabels.artists;
  const allArtists = state.artist.myArtists;

  const labelArtistsDict = labels.reduce((acc: LabelArtistsDict, label) => {
    acc[label.id] = (relations[label.id] || []).reduce(
      (acc: (SimpleArtist | LabelArtistsItem)[], rel) => {
        const existingArtist = allArtists.find((a) => a.id === rel.artistId);
        if (!existingArtist) {
          acc.push({
            id: rel.artistId,
            name: rel.artistName,
            avatar: rel.artistAvatar || '',
            type: 'band',
            shortId: '',
            created_at: new Date(rel.created_at),
            updated_at: new Date(rel.updated_at),
          });
        } else if (existingArtist.isActive !== false) {
          acc.push({ ...existingArtist });
        }

        return acc;
      },
      []
    );
    return acc;
  }, {});

  const labelsList = labels.filter(
    (label) => !!labelArtistsDict[label.id].length
  );

  return {
    currentUserId: state.user.profile?.id || '',
    labels: labelsList as RecordLabel[],
    labelArtistsDict,
    activeLabel: state.recordLabels.active?.id,
    artists: state.artist.myArtists?.filter((artist) => artist.isActive),
    labelSubscriptions: state.subscriptionManager.summary?.recordLabels,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setActive: (label: RecordLabel) => dispatch(new SetActiveLabel(label)),
});

export const LabelMenuSelectorCtrl: React.FC<LabelMenuSelectorCtrlProps> = ({
  currentUserId,
  labels,
  labelArtistsDict,
  activeLabel,
  labelSubscriptions,
  artists,
  onChange,
}) => {
  const [labelsWithLastAccess, setLabelsWithLastAccess] =
    React.useState<RecordLabel[]>(labels);

  React.useEffect(() => {
    const sortedLabelsWithLastAccess = labels
      .filter(
        (label) => labelSubscriptions?.find((s) => s.id == label.id)?.active
      )
      .map((label) => {
        const artists = labelArtistsDict[label.id];
        const lastAccess = artists.reduce((acc, artist) => {
          if (artist.lastAccess && artist.lastAccess > acc) {
            return artist.lastAccess;
          }
          return acc;
        }, 0);
        return { ...label, lastAccess };
      })
      .sort((a, b) => {
        if ((a.lastAccess || 0) < (b.lastAccess || 0)) {
          return 1;
        }
        if ((a.lastAccess || 0) > (b.lastAccess || 0)) {
          return -1;
        }

        return 0;
      });
    setLabelsWithLastAccess(sortedLabelsWithLastAccess);
  }, [labels, labelArtistsDict]);

  const handleArtistClick = () => {
    onChange();
  };

  return (
    <>
      {labelsWithLastAccess.map((label) => (
        <LabelWrapper
          key={`label-menu-list-item-${label.id}`}
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            return false;
          }}
        >
          <LabelListItem
            label={label}
            artistQty={labelArtistsDict[label.id].length}
            isOwner={label.ownerId === currentUserId}
          />
          {label.ownerId === currentUserId && label.id === activeLabel ? (
            <LabelMenuOptions dealsEnabled={!!label.flags?.deals} />
          ) : null}
          <ArtistMenuSelectorCtrl
            className="artist-menu-selector"
            labelName={label.name}
            artists={labelArtistsDict[label.id].sort((a, b) => {
              if ((a.lastAccess || 0) < (b.lastAccess || 0)) {
                return 1;
              }
              if ((a.lastAccess || 0) > (b.lastAccess || 0)) {
                return -1;
              }

              return 0;
            })}
          />
        </LabelWrapper>
      ))}
      <ArtistMenuSelectorCtrl
        artists={artists}
        onArtistSelected={handleArtistClick}
      />
    </>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LabelMenuSelectorCtrl);

const LabelWrapper = styled.div`
  margin-top: 14px;
`;
